import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  Signal,
  computed,
  effect,
  inject,
  input,
} from '@angular/core'

import { GridApi, GridOptions } from 'ag-grid-community'

import { FlxResourcesGridComponent } from '@flowx/designer/modules/projects/components/resources-grid/resources-grid.component'
import { FlxDateCellComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/date-cell.component'
import { StatusCellComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/status-cell/status-cell.component'
import { TAG_SEVERITY, TagSeverity } from '@flowx/designer/shared/entities/tag-severity'

import { InstanceNotificationsService } from './instance-notifications.service'

@Component({
  selector: 'flx-instance-notifications',
  templateUrl: './instance-notifications.component.html',
  imports: [FlxResourcesGridComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [InstanceNotificationsService],
})
export class InstanceNotificationsComponent implements OnDestroy {
  #notificationsService = inject(InstanceNotificationsService)

  processInstanceUuid = input<string>(null)

  gridOptions: Signal<GridOptions> = computed(() => ({
    rowModelType: 'infinite',
    datasource: this.#notificationsService.gridDataSource(),
  }))

  readonly notificationsTableColumns = [
    { headerName: 'Notification Type', field: 'notificationType', minWidth: 150 },
    {
      headerName: 'Status',
      field: 'status',
      minWidth: 112, // Fits the DELIVERED status label.
      cellRenderer: StatusCellComponent,
      cellRendererParams: {
        getSeverity: (value: string): TagSeverity => {
          switch (value?.toUpperCase()) {
            case 'DELIVERED':
              return TAG_SEVERITY.SUCCESS
            case 'FAILED':
            case 'ERROR':
              return TAG_SEVERITY.DANGER
            default:
              return TAG_SEVERITY.INFO
          }
        },
      },
    },
    { headerName: 'Data Source Name', field: 'emailConnectionName', minWidth: 150 },
    { headerName: 'Template Name', field: 'templateName', minWidth: 150 },
    { headerName: 'Process Name', field: 'sourceResourceName', minWidth: 150 },
    {
      headerName: 'Sent At',
      field: 'sentDate',
      cellRenderer: FlxDateCellComponent,
      flex: 0,
      width: 220,
    },
  ]

  constructor() {
    effect(() => {
      this.#notificationsService.setInitialQuery({
        processInstanceUuid: this.processInstanceUuid(),
      })
    })
  }

  onGridReady(gridApi: GridApi): void {
    this.#notificationsService.setGridApi(gridApi)
  }

  ngOnDestroy(): void {
    this.#notificationsService.reset()
  }
}
